import React, { useEffect, useState } from 'react';
import { Trash2, Info, Save, Plug, Pencil, Check, X, Copy, Send, Bell, KeyRound, AlertTriangle } from 'lucide-react';
import { useClerk, useReverification } from '@clerk/react';
import { isReverificationCancelledError } from '@clerk/react/errors';
import Layout from '../components/Layout';
import ConnectionModal from '../components/ConnectionModal';
import ConnectClickFunnelsModal from '../components/ConnectClickFunnelsModal';
import { useAuth } from '../lib/auth';
import { usersApi, oauthApi, notificationsApi } from '../lib/api';
import type { OAuthConnection, NotificationSettings, NotificationTestResult } from '../lib/api';
import { Card, PageHeader, Button, Badge, Spinner } from '../components/ui';

const EVENT_LABELS: Record<string, string> = {
  'backup.completed': 'Backup completed',
  'backup.failed': 'Backup failed',
  'restore.completed': 'Restore completed',
  'restore.failed': 'Restore failed',
  'archive.completed': 'Archive completed',
  'archive.failed': 'Archive failed',
};

function VaultNotificationsSection() {
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [events, setEvents] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<NotificationTestResult | null>(null);
  const [notifError, setNotifError] = useState<string | null>(null);
  const [notifSuccess, setNotifSuccess] = useState<string | null>(null);
  const [copiedSecret, setCopiedSecret] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await notificationsApi.get();
        if (res.success && res.data) {
          applySettings(res.data.settings);
        } else {
          setNotifError(res.message || 'Failed to load notification settings');
        }
      } catch {
        setNotifError('Failed to load notification settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const applySettings = (s: NotificationSettings) => {
    setSettings(s);
    setWebhookUrl(s.webhookUrl || '');
    setEmailEnabled(s.emailEnabled);
    setEvents(s.events);
  };

  const toggleEvent = (event: string) => {
    setEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event]
    );
  };

  const handleSave = async () => {
    setNotifError(null);
    setNotifSuccess(null);
    setTestResult(null);

    const url = webhookUrl.trim();
    if (url && !url.startsWith('https://')) {
      setNotifError('Webhook URL must start with https://');
      return;
    }

    setSaving(true);
    try {
      const res = await notificationsApi.update({ webhookUrl: url, emailEnabled, events });
      if (res.success && res.data) {
        applySettings(res.data.settings);
        setNotifSuccess('Notification settings saved');
      } else {
        setNotifError(res.message || 'Failed to save notification settings');
      }
    } catch {
      setNotifError('Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setNotifError(null);
    setNotifSuccess(null);
    setTestResult(null);
    setTesting(true);
    try {
      const res = await notificationsApi.test();
      if (res.success && res.data) {
        setTestResult(res.data);
      } else if (res.error === 'not_configured') {
        setNotifError('Nothing to test yet — configure a webhook URL or enable email first, then save.');
      } else {
        setNotifError(res.message || 'Test failed');
      }
    } catch {
      setNotifError('Test failed');
    } finally {
      setTesting(false);
    }
  };

  const copySecret = () => {
    if (!settings?.webhookSecret) return;
    navigator.clipboard.writeText(settings.webhookSecret);
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 2000);
  };

  const availableEvents = settings?.availableEvents?.length
    ? settings.availableEvents
    : Object.keys(EVENT_LABELS);

  return (
    <Card padded className="mt-5">
      <div className="flex items-center gap-2">
        <Bell className="w-4 h-4 text-accent-ink" />
        <h3 className="font-display text-base font-semibold text-ink">Vault Notifications</h3>
      </div>
      <p className="text-[13px] text-ink-2 mt-1">
        Get notified by webhook or email when vault backups, restores, and archives complete or fail.
      </p>

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <div className="mt-5 space-y-5 max-w-2xl">
          <div>
            <label className="ds-label">Webhook URL</label>
            <input
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://example.com/hooks/myleadsyncer"
              className="ds-input"
            />
            <p className="text-xs text-ink-3 mt-1">
              A public https URL — IPs and localhost are not allowed. Leave empty to disable webhook notifications.
            </p>
          </div>

          {settings?.webhookSecret && (
            <div>
              <label className="ds-label">Webhook Signing Secret</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={settings.webhookSecret}
                  readOnly
                  className="ds-input font-mono flex-1"
                />
                <button
                  onClick={copySecret}
                  className="p-2.5 bg-surface-2 rounded-[11px] border border-line text-ink-3 hover:text-ink transition-colors flex-shrink-0"
                  title="Copy secret"
                >
                  {copiedSecret ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-ink-3 mt-1">
                We sign every payload with HMAC-SHA256 using this secret — verify the X-MLS-Signature header.
              </p>
            </div>
          )}

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={emailEnabled}
              onChange={(e) => setEmailEnabled(e.target.checked)}
              className="rounded border-line text-accent focus:ring-accent-soft"
            />
            <span className="text-sm text-ink">Also send email notifications to my account email</span>
          </label>

          <div>
            <label className="ds-label">Events</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {availableEvents.map((event) => (
                <label key={event} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={events.includes(event)}
                    onChange={() => toggleEvent(event)}
                    className="rounded border-line text-accent focus:ring-accent-soft"
                  />
                  <span className="text-sm text-ink-2">{EVENT_LABELS[event] || event}</span>
                </label>
              ))}
            </div>
          </div>

          {notifError && (
            <div className="rounded-[11px] bg-danger-soft p-4">
              <p className="text-sm text-danger">{notifError}</p>
            </div>
          )}

          {notifSuccess && (
            <div className="rounded-[11px] bg-success-soft p-4">
              <p className="text-sm text-success">{notifSuccess}</p>
            </div>
          )}

          {testResult && (
            <div className="rounded-[11px] bg-surface-2 border border-line p-4 space-y-1.5">
              <p className="text-sm font-semibold text-ink">Test results</p>
              <p className="text-sm text-ink-2">
                Webhook:{' '}
                {!testResult.webhook.attempted ? (
                  <span className="text-ink-3">not configured</span>
                ) : testResult.webhook.ok ? (
                  <span className="text-success">delivered{testResult.webhook.status ? ` (HTTP ${testResult.webhook.status})` : ''}</span>
                ) : (
                  <span className="text-danger">
                    failed{testResult.webhook.status ? ` (HTTP ${testResult.webhook.status})` : ''}{testResult.webhook.error ? ` — ${testResult.webhook.error}` : ''}
                  </span>
                )}
              </p>
              <p className="text-sm text-ink-2">
                Email:{' '}
                {!testResult.email.attempted ? (
                  <span className="text-ink-3">not enabled</span>
                ) : testResult.email.ok ? (
                  <span className="text-success">sent</span>
                ) : (
                  <span className="text-danger">failed{testResult.email.error ? ` — ${testResult.email.error}` : ''}</span>
                )}
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="ghost" icon={Send} onClick={handleTest} disabled={testing}>
              {testing ? 'Sending...' : 'Send Test'}
            </Button>
            <Button icon={Save} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

/** Password, email, MFA and sessions are managed by Clerk. */
function AccountSecurity() {
  const clerk = useClerk();
  const { user } = useAuth();
  const [aiState, setAiState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');

  const disconnectAi = async () => {
    if (!confirm('Disconnect every AI client (Claude, Cursor, etc.) from your account? They will need to be approved again.')) return;
    setAiState('busy');
    try {
      const res = await usersApi.disconnectAiClients();
      setAiState(res.success ? 'done' : 'error');
    } catch {
      setAiState('error');
    }
  };

  return (
    <Card padded className="mb-5">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-base font-semibold text-ink">Sign-in &amp; Security</h3>
          <p className="text-[13px] text-ink-2 mt-1">Change your password, email address, two-step verification and active sessions.</p>
        </div>
        <Button variant="ghost" icon={KeyRound} onClick={() => clerk.openUserProfile()}>
          Manage account
        </Button>
      </div>
      {!user?.actor && (
        <div className="flex justify-between items-center gap-4 flex-wrap mt-5 pt-5 border-t border-line">
          <div>
            <h4 className="font-display text-[14px] font-semibold text-ink">AI clients</h4>
            <p className="text-[13px] text-ink-2 mt-1">
              {aiState === 'done' ? 'All AI clients were disconnected.'
                : aiState === 'error' ? 'Could not disconnect AI clients. Please try again.'
                : 'Remove access for every AI client connected through the MyLeadSyncer connector.'}
            </p>
          </div>
          <Button variant="ghost" onClick={disconnectAi} disabled={aiState === 'busy'}>
            {aiState === 'busy' ? 'Disconnecting…' : 'Disconnect all AI clients'}
          </Button>
        </div>
      )}
    </Card>
  );
}

/** Account deletion goes through our API (it owns the data), then signs out. */
function DeleteAccount() {
  const { user, logout } = useAuth();
  const [confirmText, setConfirmText] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Deleting is about the agency's own login, never a client workspace.
  const inWorkspace = !!user?.actor;
  // The API demands a recent identity check; this shows Clerk's verify-again modal and retries.
  const deleteWithReverify = useReverification(() => usersApi.deleteAccount());

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    let res;
    try {
      res = await deleteWithReverify();
    } catch (e) {
      setDeleteError(isReverificationCancelledError(e) ? 'Identity check cancelled. Your account was not deleted.' : 'Failed to delete account');
      setDeleting(false);
      return;
    }
    if (!res.success) {
      setDeleteError(res.message || 'Failed to delete account');
      setDeleting(false);
      return;
    }
    await logout();
  };

  if (inWorkspace) return null;

  return (
    <Card padded className="mt-5 border-danger/30">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-base font-semibold text-danger">Delete Account</h3>
          <p className="text-[13px] text-ink-2 mt-1">Permanently delete your account and all of its data. This cannot be undone.</p>
        </div>
        {!showDelete && (
          <Button variant="danger" icon={Trash2} onClick={() => setShowDelete(true)}>Delete account</Button>
        )}
      </div>
      {showDelete && (
        <div className="mt-5 max-w-md space-y-3">
          <div className="flex items-start gap-2.5 p-3 rounded-[11px] bg-danger-soft">
            <AlertTriangle className="w-4 h-4 text-danger flex-shrink-0 mt-0.5" />
            <p className="m-0 text-[13px] font-medium text-danger">
              Forms, quizzes, webhooks, connections, backups and submissions will be removed. Type <strong>DELETE</strong> to confirm.
            </p>
          </div>
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="ds-input"
            placeholder="DELETE"
            autoComplete="off"
          />
          {deleteError && <p className="text-sm text-danger">{deleteError}</p>}
          <div className="flex gap-2.5">
            <Button variant="ghost" onClick={() => { setShowDelete(false); setConfirmText(''); setDeleteError(null); }} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="danger" icon={Trash2} onClick={() => void handleDelete()} disabled={confirmText !== 'DELETE' || deleting}>
              {deleting ? 'Deleting…' : 'Permanently delete'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [connections, setConnections] = useState<OAuthConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedConnection, setSelectedConnection] = useState<OAuthConnection | null>(null);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editingConnId, setEditingConnId] = useState<string | null>(null);
  const [editingConnName, setEditingConnName] = useState('');

  const handleRenameConnection = async (connectionId: string) => {
    const trimmed = editingConnName.trim();
    if (!trimmed) return;
    try {
      const response = await oauthApi.updateName(connectionId, trimmed);
      if (response.success) {
        setConnections(connections.map((c) =>
          c.id === connectionId ? { ...c, connectionName: trimmed } : c
        ));
        setEditingConnId(null);
      } else {
        setError(response.message || 'Failed to rename connection');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename connection');
    }
  };

  const handleDeleteConnection = async (connectionId: string) => {
    if (!confirm('Are you sure you want to remove this connection?')) {
      return;
    }

    setError(null);

    try {
      const response = await oauthApi.delete(connectionId);
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete connection');
      }
      setConnections((prev) => prev.filter((c) => c.id !== connectionId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete connection');
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    try {
      // Update profile name
      const profileResponse = await usersApi.updateProfile(
        profile.firstName,
        profile.lastName
      );
      if (!profileResponse.success) {
        throw new Error(profileResponse.message || 'Failed to update profile');
      }

      setSuccessMessage('Profile updated successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    }
  };

  const handleConnected = (connection: OAuthConnection) => {
    setConnections((prev) => [connection, ...prev]);
  };

  useEffect(() => {
    const loadConnections = async () => {
      try {
        const response = await oauthApi.getConnections();
        if (response.success && response.data) {
          const connData = response.data as unknown as { connections: OAuthConnection[] };
          setConnections(
            (connData.connections || [])
              .sort((a, b) => b.createdAt - a.createdAt)
          );
        }
      } catch (err) {
        console.error('Error loading connections:', err);
        setError('Failed to load connections');
      } finally {
        setLoading(false);
      }
    };

    loadConnections();
  }, []);

  useEffect(() => {
    if (user) {
      setProfile((prev) => ({
        ...prev,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
      }));
    }
  }, [user]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Spinner />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader eyebrow="Account" title="Settings" description="Manage your profile, sign-in, and ClickFunnels connections." />

      {/* Profile Section */}
      <Card padded className="mb-5">
        <h3 className="font-display text-base font-semibold text-ink">Profile Settings</h3>
        <p className="text-[13px] text-ink-2 mt-1">Update your name.</p>
        <form onSubmit={handleProfileUpdate} className="mt-6 space-y-6 max-w-2xl">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="ds-label">First Name</label>
              <input
                type="text"
                value={profile.firstName}
                onChange={(e) =>
                  setProfile((prev) => ({ ...prev, firstName: e.target.value }))
                }
                className="ds-input"
              />
            </div>
            <div>
              <label className="ds-label">Last Name</label>
              <input
                type="text"
                value={profile.lastName}
                onChange={(e) =>
                  setProfile((prev) => ({ ...prev, lastName: e.target.value }))
                }
                className="ds-input"
              />
            </div>
          </div>

          {error && (
            <div className="rounded-[11px] bg-danger-soft p-4">
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          {successMessage && (
            <div className="rounded-[11px] bg-success-soft p-4">
              <p className="text-sm text-success">{successMessage}</p>
            </div>
          )}

          <div className="flex justify-end">
            <Button type="submit" icon={Save}>Save Changes</Button>
          </div>
        </form>
      </Card>

      <AccountSecurity />

      {/* Connections Section */}
      <Card padded>
        <div className="flex justify-between items-center gap-4 flex-wrap">
          <div>
            <h3 className="font-display text-base font-semibold text-ink">ClickFunnels Connections</h3>
            <p className="text-[13px] text-ink-2 mt-1">Authorize accounts where your leads sync.</p>
          </div>
          <Button icon={Plug} onClick={() => setShowConnectModal(true)}>
            Connect to ClickFunnels
          </Button>
        </div>

        {connections.length === 0 ? (
          <div className="py-10 text-center">
            <p className="text-ink-2">No active connections found.</p>
            <p className="text-sm text-ink-3 mt-2">
              Click the &quot;Connect to ClickFunnels&quot; button above to add a new connection.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto mt-5 -mx-5">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-2">
                  <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Connection Name</th>
                  <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Created</th>
                  <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Team</th>
                  <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">URL</th>
                  <th className="text-left px-5 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody>
                {connections.map((connection) => (
                  <tr key={connection.id} className="border-t border-line-2 hover:bg-surface-2 transition-colors">
                    <td className="px-5 py-3 text-[13px] text-ink whitespace-nowrap">
                      {editingConnId === connection.id ? (
                        <div className="flex items-center space-x-1">
                          <input
                            type="text"
                            value={editingConnName}
                            onChange={(e) => setEditingConnName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRenameConnection(connection.id);
                              if (e.key === 'Escape') setEditingConnId(null);
                            }}
                            autoFocus
                            className="ds-input !py-1 !px-2 text-sm w-auto"
                          />
                          <button
                            onClick={() => handleRenameConnection(connection.id)}
                            className="p-1 text-success hover:bg-success-soft rounded"
                            title="Save"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingConnId(null)}
                            className="p-1 text-ink-3 hover:bg-surface-2 rounded"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-1 group">
                          <span>{connection.connectionName || connection.workspaceName || 'ClickFunnels'}</span>
                          <button
                            onClick={() => {
                              setEditingConnId(connection.id);
                              setEditingConnName(connection.connectionName || connection.workspaceName || 'ClickFunnels');
                            }}
                            className="p-0.5 text-ink-3 hover:text-accent opacity-0 group-hover:opacity-100 transition-opacity rounded"
                            title="Rename"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <Badge tone="success" className="ml-2">Active</Badge>
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-[13px] text-ink whitespace-nowrap">
                      {new Date(connection.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3 text-[13px] text-ink whitespace-nowrap">
                      {connection.teamName}
                    </td>
                    <td className="px-5 py-3 text-[13px] text-ink whitespace-nowrap">
                      {connection.workspaceUrl && (
                        <a
                          href={connection.workspaceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent-ink hover:underline font-mono"
                        >
                          {connection.workspaceUrl}
                        </a>
                      )}
                    </td>
                    <td className="px-5 py-3 text-[13px] whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setSelectedConnection(connection)}
                          className="p-1.5 text-ink-3 hover:text-accent rounded transition-colors"
                          title="View Details"
                        >
                          <Info className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteConnection(connection.id)}
                          className="p-1.5 text-ink-3 hover:text-danger rounded transition-colors"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Vault Notifications Section */}
      <VaultNotificationsSection />

      <DeleteAccount />

      {selectedConnection && (
        <ConnectionModal
          connection={selectedConnection}
          onClose={() => setSelectedConnection(null)}
        />
      )}

      {showConnectModal && (
        <ConnectClickFunnelsModal
          onClose={() => setShowConnectModal(false)}
          onConnected={handleConnected}
        />
      )}
    </Layout>
  );
}
